import { Link } from "react-router-dom";
import { Users, Facebook, MessageSquare, BarChart3, Activity, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

const ICON_MAP = { Users, Facebook, MessageSquare, BarChart3 };

const COLOR_STYLES: Record<
  string,
  { iconBg: string; iconText: string; hoverBg: string }
> = {
  orange: {
    iconBg: "bg-amber-50 dark:bg-amber-950/40 border border-amber-200/60 dark:border-amber-800/50",
    iconText: "text-amber-600 dark:text-amber-400",
    hoverBg: "group-hover:border-amber-200 dark:group-hover:border-amber-700",
  },
  blue: {
    iconBg: "bg-blue-50 dark:bg-blue-950/40 border border-blue-200/60 dark:border-blue-800/50",
    iconText: "text-blue-600 dark:text-blue-400",
    hoverBg: "group-hover:border-blue-200 dark:group-hover:border-blue-700",
  },
  green: {
    iconBg: "bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200/60 dark:border-emerald-800/50",
    iconText: "text-emerald-600 dark:text-emerald-400",
    hoverBg: "group-hover:border-emerald-200 dark:group-hover:border-emerald-700",
  },
  purple: {
    iconBg: "bg-purple-50 dark:bg-purple-950/40 border border-purple-200/60 dark:border-purple-800/50",
    iconText: "text-purple-600 dark:text-purple-400",
    hoverBg: "group-hover:border-purple-200 dark:group-hover:border-purple-700",
  },
};

export interface QuickAction {
  id: string;
  title: string;
  description: string;
  href: string;
  icon: string;
  color: string;
}

export function QuickActionsList({ actions }: { actions: QuickAction[] }) {
  return (
    <div className="space-y-1.5">
      {(actions ?? []).map((action) => {
        const IconComponent = ICON_MAP[action.icon as keyof typeof ICON_MAP] ?? Activity;
        const colorCfg = COLOR_STYLES[action.color] ?? {
          iconBg: "bg-slate-50 dark:bg-slate-800 border border-slate-200/60 dark:border-slate-700",
          iconText: "text-slate-600 dark:text-slate-300",
          hoverBg: "group-hover:border-slate-200 dark:group-hover:border-slate-700",
        };

        return (
          <Link
            key={action.id}
            to={action.href}
            className="group flex items-center justify-between gap-3 rounded-xl border border-transparent p-2.5 transition-all duration-150 hover:border-slate-200 dark:hover:border-slate-800 hover:bg-slate-50/80 dark:hover:bg-slate-800/60 hover:shadow-2xs"
          >
            <div className="flex min-w-0 items-center gap-3">
              <div
                className={cn(
                  "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg shadow-2xs transition-transform duration-150 group-hover:scale-105",
                  colorCfg.iconBg,
                  colorCfg.iconText
                )}
              >
                <IconComponent className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="text-xs font-semibold text-slate-900 dark:text-slate-100 transition-colors group-hover:text-primary dark:group-hover:text-primary">
                  {action.title}
                </div>
                <div className="truncate text-[11px] text-slate-500 dark:text-slate-400">
                  {action.description}
                </div>
              </div>
            </div>

            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-300 dark:text-slate-600 transition-transform duration-150 group-hover:translate-x-0.5 group-hover:text-slate-600 dark:group-hover:text-slate-300" />
          </Link>
        );
      })}
    </div>
  );
}
